/**
 * UPT-2096 (P1-a, §3.4) — lecture d'une erreur des routes de prototypes :
 * chaque code a son libellé i18n (`moduleStudio.prototype.error.<code>`) ; le
 * `message` du serveur n'est jamais affiché. AUCUNE erreur n'est silencieuse :
 * toute promesse d'écriture a un catch qui affiche le libellé (role="alert").
 */
import { PROTOTYPE_ERROR_STATUS, type PrototypeErrorCode } from '../data/contracts';

export type PrototypeUiErrorCode = PrototypeErrorCode | 'upload.too_large' | 'forbidden' | 'network' | 'unknown';

export interface PrototypeUiError {
  code: PrototypeUiErrorCode;
  status: number | null;
}

const KNOWN_CODES = new Set<string>([...Object.keys(PROTOTYPE_ERROR_STATUS), 'upload.too_large']);

const readBlobCode = async (blob: Blob): Promise<string | null> => {
  try {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsText(blob, 'utf-8');
    });
    const parsed = JSON.parse(text) as { code?: unknown };
    return typeof parsed.code === 'string' ? parsed.code : null;
  } catch {
    return null;
  }
};

/** Traduit une erreur axios / réseau en code d'interface stable. */
export const readPrototypeError = async (error: unknown): Promise<PrototypeUiError> => {
  const candidate = error as {
    response?: { status?: number; data?: unknown };
    request?: unknown;
  };
  const response = candidate?.response;
  if (response && typeof response.status === 'number') {
    if (response.status === 403) return { code: 'forbidden', status: 403 };
    let code: string | null = null;
    const data = response.data;
    if (data && typeof data === 'object' && !(data instanceof Blob)) {
      const raw = (data as { code?: unknown }).code;
      if (typeof raw === 'string') code = raw;
    } else if (data instanceof Blob) {
      code = await readBlobCode(data);
    }
    if (code && KNOWN_CODES.has(code)) {
      return { code: code as PrototypeUiErrorCode, status: response.status };
    }
    if (response.status === 413) return { code: 'prototype.too_large', status: 413 };
    return { code: 'unknown', status: response.status };
  }
  if (candidate?.request) {
    return { code: 'network', status: null };
  }
  return { code: 'unknown', status: null };
};
